// src/hooks/useRealtimeCategoryInsights.ts
import { useEffect, useState, useCallback } from 'react';
import { supabase, isRealtimeEnabled } from '@/lib/supabase';
import { CategoryInsight, RealtimePayload } from '@/types/database';


interface UseRealtimeCategoryInsightsOptions {
  userId: string | null;
  initialData?: CategoryInsight[];
  onCategoryProcessed?: (category: CategoryInsight) => void;
}

export const useRealtimeCategoryInsights = ({
  userId,
  initialData = [],
  onCategoryProcessed,
}: UseRealtimeCategoryInsightsOptions) => {
  const [categories, setCategories] = useState<CategoryInsight[]>(initialData);
  const [processingCategories, setProcessingCategories] = useState<Set<string>>(new Set());
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    setCategories(initialData);
  }, [initialData]);

  const handleCategoryUpdate = useCallback(
    (payload: any) => {
      const updated = payload.new as CategoryInsight;

      if (!updated.is_processed) {
        setProcessingCategories((prev) => new Set(prev).add(updated.category_name));
      } else {
        setProcessingCategories((prev) => {
          const next = new Set(prev);
          next.delete(updated.category_name);
          return next;
        });

        setCategories((prev) => {
          const exists = prev.find((c) => c.category_name === updated.category_name);
          if (exists) {
            return prev.map((c) =>
              c.category_name === updated.category_name ? updated : c
            );
          }
          return [...prev, updated];
        });



        if (onCategoryProcessed) {
          onCategoryProcessed(updated);
        }
      }
    },
    [onCategoryProcessed]
  );

  const handleCategoryInsert = useCallback(
    (payload: any) => {
      const newCategory = payload.new as CategoryInsight;

      if (newCategory.is_processed) {
        setCategories((prev) => {
          const exists = prev.find((c) => c.category_name === newCategory.category_name);
          if (exists) {
            return prev.map((c) =>
              c.category_name === newCategory.category_name ? newCategory : c
            );
          }
          return [...prev, newCategory];
        });



        if (onCategoryProcessed) {
          onCategoryProcessed(newCategory);
        }
      } else {
        setProcessingCategories((prev) => new Set(prev).add(newCategory.category_name));
      }
    },
    [onCategoryProcessed]
  );

  useEffect(() => {
    if (!userId || !isRealtimeEnabled() || !supabase) {
      console.log('[Realtime] Skipping subscription - userId:', userId, 'enabled:', isRealtimeEnabled());
      return;
    }

    console.log('[Realtime] Setting up category insights subscription for user:', userId);

    const channel = supabase
      .channel('category-insights-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'category_insights',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          console.log('[Realtime] Category UPDATE event:', payload);
          handleCategoryUpdate(payload);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'category_insights',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          console.log('[Realtime] Category INSERT event:', payload);
          handleCategoryInsert(payload);
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Category channel status:', status);
        setIsConnected(status === 'SUBSCRIBED');
      });

    return () => {
      console.log('[Realtime] Cleaning up category insights subscription');
      if (supabase) {
        supabase.removeChannel(channel);
      }
      setIsConnected(false);
    };
  }, [userId, handleCategoryUpdate, handleCategoryInsert]);

  return {
    categories,
    processingCategories,
    isConnected,
    setCategories,
  };
};
